import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MessageType } from "@/types/message";
import { RETRYABLE_DECRYPT_PLACEHOLDERS } from "@/utils/encryptionProcessor";

/**
 * In-memory cache of already-decrypted conversation messages, keyed by conversation address.
 *
 * Switching to a cached conversation renders instantly from here (no network round-trip and,
 * crucially, no re-decryption) while a background incremental fetch picks up anything new.
 * In-memory only — a page refresh re-fetches. Bounded by an LRU cap.
 */

export interface ConversationCacheEntry {
  messages: MessageType[];
  maxSmsgId: number;
  maxRmsgId: number;
  // True if any cached message holds a retryable 🔒 placeholder (keys weren't ready when
  // cached). Gates the full re-fetch retry on re-open — an offset incremental can't retry
  // an existing 🔒 row.
  hasUndecrypted: boolean;
  // This conversation's per-address key version (control.keyExchangeVersions) at write
  // time — the retry above runs only after it moves (a key for THIS conversation arrived).
  keyExchangeVersion: number;
  lastAccess: number;
}

// An encrypted message still showing a *retryable* decryption-failure placeholder — one whose
// missing key material may yet arrive. Exact-matched against RETRYABLE_DECRYPT_PLACEHOLDERS
// (encryptionProcessor owns the retryable-vs-permanent split). Permanent failures are excluded
// so one unrecoverable message doesn't force a full history refetch on every re-open; exact
// matching also stops decrypted user text starting with 🔒 from being misclassified.
export const messageNeedsDecryptRetry = (m: MessageType): boolean =>
  !!m.isEncrypted && RETRYABLE_DECRYPT_PLACEHOLDERS.has(m.message);

interface ConversationCacheState {
  entries: Record<string, ConversationCacheEntry>;
}

// Cap the number of cached conversations to bound memory. lastAccess is stamped on writes
// (every cache hit is followed by a background refresh that re-writes, so ≈ least-recently-used).
const CACHE_LIMIT = 10;

const initialState: ConversationCacheState = { entries: {} };

// controlSlice's deleteConversation thunk, by action type (see extraReducers below).
const DELETE_CONVERSATION_FULFILLED = "chat/deleteConversation/fulfilled" as const;

const conversationCacheSlice = createSlice({
  name: "conversationCache",
  initialState,
  reducers: {
    setCacheEntry: (
      state,
      action: PayloadAction<{
        address: string;
        messages: MessageType[];
        maxSmsgId: number;
        maxRmsgId: number;
        keyExchangeVersion: number;
      }>
    ) => {
      const { address, messages, maxSmsgId, maxRmsgId, keyExchangeVersion } = action.payload;
      state.entries[address] = {
        messages,
        maxSmsgId,
        maxRmsgId,
        hasUndecrypted: messages.some(messageNeedsDecryptRetry),
        keyExchangeVersion,
        lastAccess: Date.now(),
      };

      const keys = Object.keys(state.entries);
      if (keys.length > CACHE_LIMIT) {
        let oldest = keys[0];
        for (const k of keys) {
          if (state.entries[k].lastAccess < state.entries[oldest].lastAccess) oldest = k;
        }
        delete state.entries[oldest];
      }
    },
    invalidateCacheEntry: (state, action: PayloadAction<string>) => {
      delete state.entries[action.payload];
    },
  },
  extraReducers: (builder) => {
    // Never keep another user's decrypted messages around after logout.
    builder.addCase("auth/logout/fulfilled", (state) => {
      state.entries = {};
    });
    // A deleted thread must not survive here, or a new conversation at the same address would
    // hydrate the removed messages. By action type like logout above; the payload is pinned to
    // that literal because addCase's string overload requires A extends Action<Type>.
    builder.addCase(
      DELETE_CONVERSATION_FULFILLED,
      (state, action: PayloadAction<{ address: string }, typeof DELETE_CONVERSATION_FULFILLED>) => {
        delete state.entries[action.payload.address];
      }
    );
  },
});

export const { setCacheEntry, invalidateCacheEntry } = conversationCacheSlice.actions;

export default conversationCacheSlice.reducer;
